<script setup lang="ts">
  import { IconClose } from '@gat-ship/icons'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'

  const props = withDefaults(
    defineProps<
      DialogContentProps & {
        class?: HTMLAttributes['class']
        showCloseIcon?: boolean
        hideOverlay?: boolean
        /**
         * Render as a central-modal-host stack entry: reka's `<DialogOverlay>` is
         * modal-gated (renders nothing while `modal=false`) and our content is
         * nested inside it, so for host entries we swap the overlay for a plain
         * `<div>` with the identical centering markup — it renders regardless of
         * `modal` and provides the dim + page-block itself.
         */
        nonModal?: boolean
        /**
         * Render the overlay transparent and click-through. Used for buried
         * central-modal-host stack entries: they keep their content mounted but
         * must not add a second dim layer or intercept clicks meant for the top
         * entry's overlay.
         */
        overlayMuted?: boolean
        overlayZIndex?: number
        maxWidth?: number
        fullScreenMobile?: boolean
        fullScreen?: boolean
      }
    >(),
    {
      showCloseIcon: undefined,
      overlayZIndex: 50
    }
  )
  const emits = defineEmits<DialogContentEmits>()

  const delegatedProps = computed(() => {
    const {
      class: _,
      maxWidth: __,
      fullScreenMobile: ___,
      fullScreen: ____,
      ...delegated
    } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  // Host stack entries (non-modal) use a plain <div> wrapper; everything else
  // keeps reka's <DialogOverlay> (its scroll-lock + dim). Same centering markup
  // either way, so the dialog looks identical.
  const overlayWrapper = computed(() =>
    props.nonModal ? 'div' : DialogOverlay
  )

  // In full-screen mode the inline maxWidth must not apply — inline
  // styles would beat the `max-w-none` class.
  const contentStyle = computed(() => ({
    zIndex: props.overlayZIndex,
    ...(props.maxWidth && !props.fullScreen
      ? { maxWidth: `${props.maxWidth}px` }
      : {})
  }))
</script>

<template>
  <DialogPortal>
    <component
      :is="overlayWrapper"
      v-if="!hideOverlay"
      :class="
        cn(
          'fixed inset-0 grid place-items-center overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          overlayMuted ? 'pointer-events-none bg-transparent' : 'bg-black/40',
          props.fullScreenMobile && 'max-md:place-items-stretch'
        )
      "
      :style="{ zIndex: overlayZIndex }"
    >
      <DialogContent
        :class="
          cn(
            'border-border bg-background relative z-50 my-8 grid w-full max-w-lg gap-4 border p-6 shadow-lg duration-200 sm:rounded-lg md:w-full',
            props.class,
            props.fullScreenMobile &&
              'max-md:my-0 max-md:h-dvh max-md:max-w-none max-md:rounded-none max-md:border-0',
            props.fullScreen &&
              'my-0 h-dvh w-screen max-w-none rounded-none border-0 sm:rounded-none'
          )
        "
        v-bind="forwarded"
        :style="contentStyle"
        @pointer-down-outside="
          (event) => {
            const originalEvent = event.detail.originalEvent
            const target = originalEvent.target as HTMLElement
            if (
              originalEvent.offsetX > target.clientWidth ||
              originalEvent.offsetY > target.clientHeight
            ) {
              event.preventDefault()
            }
          }
        "
      >
        <slot />

        <DialogClose
          v-if="showCloseIcon ?? true"
          data-testid="dialog-close"
          class="hover:bg-secondary absolute right-4 top-4 rounded-md p-0.5 transition-colors hover:cursor-pointer"
        >
          <icon-close class="size-4" />
          <span class="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </component>
  </DialogPortal>
</template>
